import { randomBytes } from './random.js';

/**
 * A donation's public reference code.
 *
 * Crockford base32 excludes `I`, `L`, `O` and `U`, so the `O`/`0` and `I`/`1`
 * confusions decode away — which matters because this string is read back off
 * a free-text bank description, off a QR code and off an OCR pass. The `BT`
 * prefix is what a matcher regexes out of a description.
 *
 * Ten characters stored: `BT`, seven symbols from a CSPRNG, and a trailing
 * check symbol. Rendered for a human as `BT-7K4Q-9MXH` and stored without the
 * dashes — see docs/decisions/0022-the-donation-record.md.
 */
export const REFERENCE_CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
export const REFERENCE_CODE_PREFIX = 'BT';
export const REFERENCE_CODE_PAYLOAD_LENGTH = 7;
export const REFERENCE_CODE_LENGTH =
  REFERENCE_CODE_PREFIX.length + REFERENCE_CODE_PAYLOAD_LENGTH + 1;

/**
 * Every weight is odd, so it is invertible modulo the 32-symbol alphabet and
 * no single mistyped symbol can leave the checksum unchanged.
 */
const CHECK_WEIGHTS = [3, 5, 7, 9, 11, 13, 15] as const;

const CONFUSABLE_SYMBOLS: Record<string, string> = { I: '1', L: '1', O: '0' };

const symbolValue = (symbol: string): number => REFERENCE_CODE_ALPHABET.indexOf(symbol);

const checkSymbol = (payload: string): string => {
  let sum = 0;
  for (let index = 0; index < payload.length; index += 1) {
    sum += (CHECK_WEIGHTS[index] ?? 1) * symbolValue(payload[index] as string);
  }
  return REFERENCE_CODE_ALPHABET[sum % REFERENCE_CODE_ALPHABET.length] as string;
};

/**
 * Uppercases, drops separators, and folds the characters Crockford leaves out
 * of the alphabet onto the digits they are mistaken for. A code copied out of
 * an email with dashes, lowercased, with a capital O for a zero, normalizes to
 * the value that was issued.
 */
export function normalizeReferenceCode(input: string): string {
  const stripped = input.toUpperCase().replace(/[^0-9A-Z]/g, '');
  return [...stripped].map((character) => CONFUSABLE_SYMBOLS[character] ?? character).join('');
}

/**
 * The stored codes a typed code, or the start of one, can begin — with or
 * without its `BT`, in any case, with spaces and dashes anywhere. A code whose
 * body itself starts with `BT` is read both ways, since `BTBT1N…` typed
 * without its prefix is `BT1N…`. Empty when nothing code-like is left.
 */
export function referenceCodePrefixes(input: string): string[] {
  const code = normalizeReferenceCode(input);
  if (code === '') return [];
  const prefixed = `${REFERENCE_CODE_PREFIX}${code}`;
  const candidates = code.startsWith(REFERENCE_CODE_PREFIX) ? [code, prefixed] : [prefixed];
  return candidates.filter((prefix) => prefix.length <= REFERENCE_CODE_LENGTH);
}

/**
 * Whether free text typed into a search box that also finds names reads as a
 * code: only Latin letters and digits once spaces and dashes are gone, at least
 * three of them, and either a digit or the `BT` prefix — a name has neither,
 * so *Ali* is never searched as `BTA11`.
 */
export function looksLikeReferenceCode(input: string): boolean {
  const compact = input.replace(/[\s-]+/g, '');
  if (compact.length < 3 || !/^[0-9A-Za-z]+$/.test(compact)) return false;
  return /[0-9]/.test(compact) || compact.toUpperCase().startsWith(REFERENCE_CODE_PREFIX);
}

export function generateReferenceCode(): string {
  const bytes = randomBytes(REFERENCE_CODE_PAYLOAD_LENGTH);
  // 256 is a whole multiple of 32, so masking keeps the draw uniform.
  const payload = [...bytes]
    .map((byte) => REFERENCE_CODE_ALPHABET[byte % REFERENCE_CODE_ALPHABET.length] as string)
    .join('');
  return `${REFERENCE_CODE_PREFIX}${payload}${checkSymbol(payload)}`;
}

export function isValidReferenceCode(input: string): boolean {
  const code = normalizeReferenceCode(input);
  if (code.length !== REFERENCE_CODE_LENGTH) return false;
  if (!code.startsWith(REFERENCE_CODE_PREFIX)) return false;

  const body = code.slice(REFERENCE_CODE_PREFIX.length);
  if ([...body].some((symbol) => symbolValue(symbol) < 0)) return false;

  const payload = body.slice(0, REFERENCE_CODE_PAYLOAD_LENGTH);
  return body.slice(REFERENCE_CODE_PAYLOAD_LENGTH) === checkSymbol(payload);
}

/** `BT7K4Q9MXH` as `BT-7K4Q-9MXH`. Never what is stored. */
export function formatReferenceCode(code: string): string {
  const normalized = normalizeReferenceCode(code);
  if (normalized.length !== REFERENCE_CODE_LENGTH) return normalized;
  return `${normalized.slice(0, 2)}-${normalized.slice(2, 6)}-${normalized.slice(6)}`;
}

const REFERENCE_CODE_BODY_LENGTH = REFERENCE_CODE_PAYLOAD_LENGTH + 1;

/**
 * Every code-shaped token in free text — `BT` and eight more characters, each
 * one a digit or a Latin capital — in the order they appear, whether or not
 * its check symbol passes.
 *
 * The text is upper-cased, spaces and dashes are dropped and `O`, `I` and `L`
 * are read as digits, as a pasted code is; **any other character ends a
 * token**. Punctuation, a non-Latin lookalike, a full-width form or an
 * invisible character is never stepped over, so no token is assembled across
 * one. Both the exact reader and the one-symbol correction read these tokens,
 * so a typo can never be read out of text the exact reader would not read.
 *
 * **A token whose check symbol passes owns its ten symbols**: reading resumes
 * after it, never inside it. Spaces are dropped, so two codes a space apart are
 * one run of symbols, and a `BT` inside the first — about one code in 150 has
 * one — would otherwise start a token spliced from both, which passes its check
 * one time in 32 and names a donation nobody wrote. A token that fails its check
 * owns nothing, so a typo still leaves the code after it readable.
 */
export function referenceCodeTokens(text: string): string[] {
  const folded = foldReferenceCodeText(text);
  const tokens: string[] = [];
  let index = 0;
  while (index + REFERENCE_CODE_LENGTH <= folded.length) {
    const token = folded.slice(index, index + REFERENCE_CODE_LENGTH);
    if (!token.startsWith(REFERENCE_CODE_PREFIX) || !TOKEN_SYMBOLS.test(token)) {
      index += 1;
      continue;
    }
    tokens.push(token);
    index += isValidReferenceCode(token) ? REFERENCE_CODE_LENGTH : 1;
  }
  return tokens;
}

const TOKEN_SYMBOLS = /^[0-9A-Z]+$/;

/**
 * Every donation reference code hiding in free text — a bank description, an
 * OCR pass over a photographed slip, a QR payload: each token of
 * `referenceCodeTokens` whose check symbol passes, so `BT-7k4q-9mxh` and
 * `BT 7K4Q 9MXH` read the same and `BT7K4.Q9MXH` reads as nothing. Codes come
 * back in the order they appear, without repeats.
 */
export function extractReferenceCodes(text: string): string[] {
  const found: string[] = [];
  for (const token of referenceCodeTokens(text)) {
    if (!isValidReferenceCode(token)) continue;
    if (!found.includes(token)) found.push(token);
  }
  return found;
}

/**
 * Every single-symbol correction of a `BT` token whose check symbol fails.
 *
 * At most one replacement per position can pass, because the check weights are
 * odd and therefore invertible modulo the 32-symbol alphabet, so the answer
 * never holds more than eight candidates — and a token that already passes has
 * nothing to correct.
 */
export function correctSingleSymbol(token: string): string[] {
  const code = normalizeReferenceCode(token);
  if (code.length !== REFERENCE_CODE_LENGTH) return [];
  if (!code.startsWith(REFERENCE_CODE_PREFIX)) return [];
  if (isValidReferenceCode(code)) return [];

  const corrections: string[] = [];
  for (let offset = 0; offset < REFERENCE_CODE_BODY_LENGTH; offset += 1) {
    const position = REFERENCE_CODE_PREFIX.length + offset;
    for (const symbol of REFERENCE_CODE_ALPHABET) {
      if (symbol === code[position]) continue;
      const variant = `${code.slice(0, position)}${symbol}${code.slice(position + 1)}`;
      if (isValidReferenceCode(variant)) corrections.push(variant);
    }
  }
  return corrections;
}

/**
 * Free text as the token readers see it: upper-cased, spaces and dashes dropped,
 * `O`, `I` and `L` read as digits, and every other character left where it is,
 * so it still ends a token.
 */
export function foldReferenceCodeText(text: string): string {
  return [...text.toUpperCase().replace(/[\s-]+/g, '')]
    .map((character) => CONFUSABLE_SYMBOLS[character] ?? character)
    .join('');
}
